/**
 * Configuration centralisée des routes publiques et protégées
 * Utilisé par AuthGuard, ProfileGuard et useProfileGuard
 */

/**
 * Routes publiques (pas d'authentification requise)
 */
export const PUBLIC_ROUTES = [
  "/auth/login",
  "/auth/sign-up",
  "/auth/sign-up-success",
  "/auth/forgot-password",
  "/auth/update-password",
  "/auth/confirm",
  "/auth/error",
  "/auth/callback",
] as const;

/**
 * Extrait le chemin sans la locale (ex: /fr/auth/login -> /auth/login)
 */
export function extractPathWithoutLocale(pathname: string): string {
  return pathname.replace(/^\/[a-z]{2}(\/|$)/, "/");
}

/**
 * Extrait la locale du pathname (ex: /fr/dashboard -> fr)
 */
export function extractLocale(pathname: string): string {
  return pathname.split("/")[1] || "fr";
}

/**
 * Vérifie si une route est publique (pas d'authentification requise)
 */
export function isPublicRoute(pathname: string): boolean {
  const pathWithoutLocale = extractPathWithoutLocale(pathname);
  return PUBLIC_ROUTES.some((route) => pathWithoutLocale === route || pathWithoutLocale.startsWith(route + "/"));
}

/**
 * Vérifie si le pathname correspond à la route onboarding
 */
export function isOnboardingRoute(pathname: string): boolean {
  const pathWithoutLocale = extractPathWithoutLocale(pathname);
  return pathWithoutLocale === "/onboarding" || pathWithoutLocale.startsWith("/onboarding/");
}

/**
 * Vérifie si le pathname correspond à la route logout
 * (route de transition, pas besoin de charger profil/rôle)
 */
export function isLogoutRoute(pathname: string): boolean {
  const pathWithoutLocale = extractPathWithoutLocale(pathname);
  return pathWithoutLocale === "/auth/logout" || pathWithoutLocale.startsWith("/auth/logout/");
}

/**
 * Vérifie si le pathname est une page d'authentification (login, sign-up, etc.)
 * Exclut la page logout qui est une route de transition
 */
export function isAuthPage(pathname: string): boolean {
  const pathWithoutLocale = extractPathWithoutLocale(pathname);
  return pathWithoutLocale.startsWith("/auth/") && !isLogoutRoute(pathname);
}
